import type { StateAction } from "@entrilia/shared/types";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  emailDomainValidator,
  maxCharactersValidator,
  requiredValidator,
  validResult,
} from "@entrilia/shared/utilities/validators";
import type {
  Inbox,
  InboxChannel,
  InboxChannelType,
  UpdateInboxChannelRequest,
  UpdateInboxRequest,
} from "../../../../api/types/inboxesTypes";

export interface EditInboxChannelState {
  isNewChannel: boolean;
  channelId?: string;
  channelForm: InboxChannelForm;
  isDirty: boolean;
  touchedFields: ValidationField[];
}

interface InboxChannelForm {
  name: string;
  description: string;
  type: InboxChannelType;
  addressField: string;
  isActive: boolean;
  agentType: string;
  incomingDomainWhitelist: WhitelistedDomain[];
}

interface WhitelistedDomain {
  id: string;
  domain: string;
}

const toWhitelistedDomain = (domain: string): WhitelistedDomain => ({ id: generateGuid(), domain });

type ValidationField = "name" | "description" | "address" | "agentType";

const mapStateToChannelUpdate = (
  state: EditInboxChannelState,
  clientCode: string,
  addressDomain: string
): UpdateInboxChannelRequest => {
  const { channelForm, channelId, isNewChannel } = state;

  const result: UpdateInboxChannelRequest = {
    isNewChannel,
    id: channelId,
    name: channelForm.name.trim(),
    description: channelForm.description.trim(),
    type: channelForm.type,
    isActive: channelForm.isActive,
    dedicatedWorkflowAgentTypes: channelForm.agentType ? [channelForm.agentType] : [],
    address: `${clientCode}-${channelForm.addressField}@${addressDomain}`,
    incomingDomainWhitelist:
      channelForm.incomingDomainWhitelist.length > 0
        ? distinct(
            channelForm.incomingDomainWhitelist.map(({ domain }) => domain.trim()).filter((domain) => domain.length > 0)
          )
        : undefined,
  };

  if (result.type !== "Email") {
    result.address = undefined;
    result.incomingDomainWhitelist = undefined;
  }

  return result;
};

export const getUpdateInboxRequest = (
  state: EditInboxChannelState,
  inbox: Inbox,
  clientCode: string
): UpdateInboxRequest => {
  const updatedChannel = mapStateToChannelUpdate(state, clientCode, inbox.addressDomain);

  const channelUpdates: UpdateInboxChannelRequest[] = updatedChannel.isNewChannel
    ? [...inbox.channels.map((channel) => ({ ...channel, isNewChannel: false })), updatedChannel]
    : inbox.channels.map((channel) =>
        channel.id === updatedChannel.id ? updatedChannel : { ...channel, isNewChannel: false }
      );

  return {
    channels: channelUpdates,
  };
};

export interface InboxChannelValidationResult {
  isValid: boolean;
  name: ValidationResult;
  description: ValidationResult;
  agentType: ValidationResult;
  address: ValidationResult;
  domainItems: ValidationResult[];
}

export const validateInboxChannel = (channel: InboxChannelForm): InboxChannelValidationResult => {
  const validateName = combineValidators(requiredValidator, maxCharactersValidator(50));
  const validateDescription = requiredValidator;
  const validateDomainItem = emailDomainValidator;

  const nameResult = validateName(channel.name);
  const descriptionResult = validateDescription(channel.description);
  const agentTypeResult = requiredValidator(channel.agentType);
  const addressResult = channel.type === "Email" ? requiredValidator(channel.addressField) : validResult();
  const domainItemsResults =
    channel.type === "Email" && channel.incomingDomainWhitelist?.length
      ? channel.incomingDomainWhitelist.map(({ domain }) => validateDomainItem(domain))
      : [];

  const isValid =
    nameResult.isValid &&
    descriptionResult.isValid &&
    agentTypeResult.isValid &&
    addressResult.isValid &&
    domainItemsResults.every((r) => r.isValid);

  return {
    isValid,
    name: nameResult,
    description: descriptionResult,
    agentType: agentTypeResult,
    address: addressResult,
    domainItems: domainItemsResults,
  };
};

const getAddressField = (address: string | undefined): string => {
  if (!address) {
    return "";
  }

  const addr = address.split("@")[0];
  if (!addr) {
    return "";
  }

  return addr.substring(addr.indexOf("-") + 1);
};

const mapChannelToForm = (channel: InboxChannel): InboxChannelForm => {
  return {
    name: channel.name,
    description: channel.description,
    type: channel.type,
    addressField: getAddressField(channel.address),
    isActive: channel.isActive,
    agentType: channel.dedicatedWorkflowAgentTypes[0] ?? "",
    incomingDomainWhitelist: (channel.incomingDomainWhitelist ?? []).map(toWhitelistedDomain),
  };
};

// Actions

export const createNewChannelAction = (): StateAction<EditInboxChannelState | undefined> => () => {
  return {
    channelForm: {
      name: "",
      description: "",
      type: "Email",
      addressField: "",
      isActive: true,
      agentType: "",
      incomingDomainWhitelist: [],
    },
    isDirty: false,
    isNewChannel: true,
    touchedFields: [],
  };
};

export const startEditingChannelAction =
  (channel: InboxChannel): StateAction<EditInboxChannelState | undefined> =>
  (state) => ({
    ...state,
    channelId: channel.id,
    channelForm: mapChannelToForm(channel),
    isDirty: false,
    isNewChannel: false,
    touchedFields: [],
  });

export const finishEditingChannelAction = (): StateAction<EditInboxChannelState | undefined> => () => undefined;

export const resetEditingChannelAction =
  (storedChannels: InboxChannel[]): StateAction<EditInboxChannelState | undefined> =>
  (state) => {
    if (state === undefined) {
      return undefined;
    }

    const storedChannel = storedChannels.find((c) => c.id === state.channelId);
    if (storedChannel === undefined) {
      return undefined;
    }

    return {
      ...state,
      channelForm: mapChannelToForm(storedChannel),
      isDirty: false,
      touchedFields: [],
    };
  };

export const changeChannelActiveStateAction =
  (isActive: boolean): StateAction<EditInboxChannelState | undefined> =>
  (state) => {
    if (state === undefined) {
      return undefined;
    }

    return {
      ...state,
      channelForm: { ...state.channelForm, isActive },
      isDirty: true,
    };
  };

export const changeChannelNameAction =
  (name: string): StateAction<EditInboxChannelState | undefined> =>
  (state) => {
    if (state === undefined) {
      return undefined;
    }

    return {
      ...state,
      channelForm: { ...state.channelForm, name },
      isDirty: true,
      touchedFields: distinct([...state.touchedFields, "name"]),
    };
  };

export const changeChannelDescriptionAction =
  (description: string): StateAction<EditInboxChannelState | undefined> =>
  (state) => {
    if (state === undefined) {
      return undefined;
    }

    return {
      ...state,
      channelForm: { ...state.channelForm, description },
      isDirty: true,
      touchedFields: distinct([...state.touchedFields, "description"]),
    };
  };

export const changeChannelTypeAction =
  (type: InboxChannelType): StateAction<EditInboxChannelState | undefined> =>
  (state) => {
    if (state === undefined) {
      return undefined;
    }

    return {
      ...state,
      channelForm: { ...state.channelForm, type, addressField: "" },
      isDirty: true,
    };
  };

export const changeChannelAddressAction =
  (addressField: string): StateAction<EditInboxChannelState | undefined> =>
  (state) => {
    if (state === undefined) {
      return undefined;
    }

    return {
      ...state,
      channelForm: { ...state.channelForm, addressField },
      isDirty: true,
      touchedFields: distinct([...state.touchedFields, "address"]),
    };
  };

export const changeAgentTypeAction =
  (agentType: string): StateAction<EditInboxChannelState | undefined> =>
  (state) => {
    if (state === undefined) {
      return undefined;
    }

    return {
      ...state,
      channelForm: { ...state.channelForm, agentType },
      isDirty: true,
      touchedFields: distinct([...state.touchedFields, "agentType"]),
    };
  };

export const changeChannelDomainAction =
  (domain: string, index: number): StateAction<EditInboxChannelState | undefined> =>
  (state) => {
    if (state === undefined) {
      return undefined;
    }

    const newDomains = state.channelForm.incomingDomainWhitelist.map((item, i) =>
      i === index ? { ...item, domain } : item
    );

    return {
      ...state,
      channelForm: { ...state.channelForm, incomingDomainWhitelist: newDomains },
      isDirty: true,
    };
  };

export const addChannelDomainAction = (): StateAction<EditInboxChannelState | undefined> => (state) => {
  if (state === undefined) {
    return undefined;
  }

  const newDomains = [...state.channelForm.incomingDomainWhitelist, toWhitelistedDomain("")];
  return {
    ...state,
    channelForm: { ...state.channelForm, incomingDomainWhitelist: newDomains },
    isDirty: true,
  };
};

export const removeChannelDomainAction =
  (index: number): StateAction<EditInboxChannelState | undefined> =>
  (state) => {
    if (state === undefined) {
      return undefined;
    }

    const newDomains = state.channelForm.incomingDomainWhitelist.filter((_, i) => i !== index);

    return {
      ...state,
      channelForm: { ...state.channelForm, incomingDomainWhitelist: newDomains },
      isDirty: true,
    };
  };
